import { Link } from "react-router-dom";
import { ArrowRight, Stethoscope, Newspaper, Award, HeartHandshake, Music2 } from "lucide-react";
import { BOOKS, IMAGES, AWARDS, JOURNEY_VIDEOS } from "../lib/content";
import ZoomableImage from "../components/site/ZoomableImage";
import VideoLightbox from "../components/site/VideoLightbox";

const CREDENTIALS = [
  {
    icon: Newspaper,
    title: "Former Presidential Correspondent",
    body: "Covered the Presidency of Ghana and national governance.",
  },
  {
    icon: Award,
    title: "Award-Winning Journalist",
    body: "More than 17 years in journalism and public affairs.",
  },
  {
    icon: Stethoscope,
    title: "Healthcare Professional",
    body: "Serving clients across Toronto\u2019s healthcare system.",
  },
  {
    icon: HeartHandshake,
    title: "Founder",
    body: "Founder of Delinam Foundation and the Climate Change & Health Advocacy Initiative.",
  },
  {
    icon: Music2,
    title: "Musician & Storyteller",
    body: "A lifelong musician whose experiences inspire books, journalism and advocacy.",
  },
];

const JOURNEY = [
  { era: "Beginnings", title: "Childhood Musician", note: "Songs before sentences. The first language was rhythm." },
  { era: "Newsroom", title: "Journalist", note: "Cutting his teeth in Ghana\u2019s public-affairs press." },
  { era: "Presidency", title: "Presidential Correspondent", note: "Inside the corridors of national power." },
  { era: "Crossing", title: "Immigrant to Canada", note: "A new country, a new vocabulary, the same conviction." },
  { era: "Frontline", title: "Healthcare Professional", note: "Toronto\u2019s wards became the next assignment." },
  { era: "Today", title: "Author & Foundation Founder", note: "Books, columns, and the Delinam Foundation \u2014 one ongoing argument." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-cream"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.12] mix-blend-multiply pointer-events-none"
          style={{
            backgroundImage: `url(${IMAGES.heroTexture})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="container-vk relative pt-20 md:pt-28 pb-16 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 animate-fade-up order-2 lg:order-1">
              <p className="overline mb-8" data-testid="hero-overline">
                Author · Journalist · Healthcare · Musician · Advocate · Founder
              </p>
              <h1
                data-testid="hero-headline"
                className="heading-display text-[3.25rem] sm:text-7xl lg:text-[6.25rem] xl:text-[7rem] font-semibold leading-[0.92] tracking-tight text-ink"
              >
                Victor
                <br />
                <span className="italic font-medium text-burgundy">
                  Kwawukume
                </span>
              </h1>

              <p
                data-testid="hero-subheadline"
                className="mt-8 text-lg md:text-xl text-ink/70 max-w-2xl leading-relaxed text-balance"
              >
                From Ghana&rsquo;s corridors of power to Canada&rsquo;s frontline
                healthcare system &mdash; stories of resilience, leadership,
                migration, service, and hope.
              </p>

              <div
                className="mt-10 flex flex-col sm:flex-row gap-4"
                data-testid="hero-ctas"
              >
                <Link
                  to="/books"
                  className="btn-primary group"
                  data-testid="hero-cta-books"
                >
                  Explore My Books
                  <ArrowRight
                    size={18}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Link>
                <Link
                  to="/about"
                  className="btn-secondary"
                  data-testid="hero-cta-about"
                >
                  About Victor
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2 animate-fade-up">
              <div className="relative aspect-[4/5] overflow-hidden border border-paperline bg-cream-deep">
                <img
                  src={IMAGES.authorPortrait}
                  alt="Victor Kwawukume — author, journalist, healthcare professional"
                  data-testid="hero-portrait"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute -bottom-px -left-px bg-cream border-t border-r border-paperline px-5 py-3">
                  <p className="overline text-gold">Victor Kwawukume</p>
                  <p className="heading-display text-ink text-base mt-0.5 italic">
                    Toronto &middot; Accra
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Credibility strip */}
          <div
            data-testid="hero-credibility-strip"
            className="mt-16 md:mt-20 border-t border-b border-paperline py-5"
          >
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-3 text-[11px] md:text-xs uppercase tracking-[0.18em] text-ink/65 font-semibold">
              <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-burgundy rounded-full" /> 17+ Years Journalism Experience</li>
              <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-burgundy rounded-full" /> Former Presidential Correspondent</li>
              <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-burgundy rounded-full" /> Award-Winning Reporter</li>
              <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-burgundy rounded-full" /> Founder, Delinam Foundation</li>
              <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-burgundy rounded-full" /> Healthcare Professional</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CREDENTIALS & IMPACT */}
      <section
        data-testid="credentials-section"
        className="bg-cream-deep py-24 md:py-32 grain"
      >
        <div className="container-vk">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-8">
              <p className="overline mb-5">Credentials & Impact</p>
              <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02] tracking-tight text-balance">
                Five vocations.{" "}
                <span className="italic text-burgundy">
                  One uncompromising standard of public service.
                </span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-ink/70 max-w-md">
                A working life arranged across newsrooms, hospitals, stages and
                a foundation &mdash; with the same reader, patient, and
                neighbour at the centre of it.
              </p>
            </div>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 stagger"
            data-testid="credentials-grid"
          >
            {CREDENTIALS.map((c, i) => {
              const Icon = c.icon;
              const wide = i === 0 || i === 4; // bento: first + last span two columns on lg
              return (
                <article
                  key={c.title}
                  data-testid={`credential-card-${i}`}
                  className={`animate-fade-up group relative bg-cream border border-paperline p-8 md:p-10 hover:border-burgundy transition-colors flex flex-col gap-6 ${
                    wide ? "lg:col-span-3" : "lg:col-span-2"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 border border-paperline flex items-center justify-center text-burgundy group-hover:bg-burgundy group-hover:text-cream transition-colors">
                      <Icon size={22} />
                    </div>
                    <span className="heading-display text-xl text-gold font-medium">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div>
                    <h3 className="heading-display text-2xl md:text-3xl font-semibold leading-[1.1]">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-ink/75 leading-relaxed">{c.body}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED BOOKS — Bento */}
      <section
        data-testid="featured-books-section"
        className="bg-cream py-24 md:py-32"
      >
        <div className="container-vk">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-8">
              <p className="overline mb-5">The Library</p>
              <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02] tracking-tight">
                Explore the{" "}
                <span className="italic text-burgundy">Catalogue</span>.
              </h2>
              <p className="mt-6 text-lg text-ink/70 max-w-2xl leading-relaxed">
                An expanding collection of books by Victor Kwawukume exploring
                identity, migration, resilience, memory, leadership, and the
                enduring human capacity for renewal.
              </p>
            </div>
            <div className="lg:col-span-4">
              <Link
                to="/books"
                data-testid="books-section-cta"
                className="link-underline text-sm uppercase tracking-[0.2em] text-ink font-semibold"
              >
                See all books →
              </Link>
            </div>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger"
            data-testid="featured-books-grid"
          >
            {BOOKS.slice(0, 4).map((b, i) => (
              <BookTile
                key={b.slug}
                book={b}
                className=""
                featured={i === 0}
              />
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY TIMELINE */}
      <section
        data-testid="journey-section"
        className="bg-cream py-24 md:py-32 border-t border-paperline"
      >
        <div className="container-vk">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-8">
              <p className="overline mb-5">The Journey</p>
              <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02] tracking-tight text-balance">
                Six chapters that became{" "}
                <span className="italic text-burgundy">one body of work</span>.
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-ink/70 max-w-md">
                From a boy with a melody in Ghana to an author building a
                foundation from Toronto &mdash; the long line that joins the
                vocations.
              </p>
            </div>
          </div>

          {/* Desktop: horizontal rail. Mobile: vertical. */}
          <ol
            data-testid="journey-timeline"
            className="relative grid grid-cols-1 lg:grid-cols-6 gap-10 lg:gap-6 stagger"
          >
            <span
              aria-hidden
              className="hidden lg:block absolute top-[34px] left-0 right-0 h-px bg-paperline"
            />
            {JOURNEY.map((j, i) => (
              <li
                key={j.title}
                data-testid={`journey-step-${i}`}
                className="animate-fade-up relative flex lg:flex-col gap-5 lg:gap-0"
              >
                {/* node */}
                <div className="relative shrink-0 lg:mb-7">
                  <span className="relative z-10 flex items-center justify-center w-[68px] h-[68px] bg-cream border border-paperline group-hover:border-burgundy heading-display text-2xl font-semibold text-burgundy">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {i < JOURNEY.length - 1 && (
                    <span
                      aria-hidden
                      className="lg:hidden absolute left-[33px] top-[68px] w-px h-[calc(100%+2.5rem)] bg-paperline"
                    />
                  )}
                </div>
                <div className="lg:pr-4">
                  <p className="overline mb-2">{j.era}</p>
                  <h3 className="heading-display text-xl md:text-2xl font-semibold leading-tight">
                    {j.title}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-ink/70 leading-relaxed">
                    {j.note}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section data-testid="about-teaser-section" className="bg-cream-deep py-24 md:py-32 grain">
        <div className="container-vk grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-paperline">
              <img
                src={IMAGES.aboutPortrait}
                alt="Victor Kwawukume at the Graphic Communications Group newsroom"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline mb-5">About Victor</p>
            <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight">
              A reporter who became a nurse. A nurse who never stopped reporting.
            </h2>
            <div className="mt-8 space-y-5 text-lg leading-relaxed text-ink/75 max-w-2xl">
              <p>
                Victor Kwawukume spent a decade in Ghana&rsquo;s executive press
                corps, covering presidents, parliaments and the policies that
                shaped a generation. He now lives in Canada, where he works in
                frontline healthcare and writes about the country he is still
                learning.
              </p>
              <p>
                His books, journalism and the Delinam Foundation are different
                expressions of the same conviction &mdash; that storytelling and
                service belong to each other.
              </p>
            </div>
            <Link
              to="/about"
              data-testid="about-teaser-cta"
              className="mt-10 inline-flex items-center gap-2 link-underline text-sm uppercase tracking-[0.2em] font-semibold text-burgundy"
            >
              Read Victor&rsquo;s Story <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* JOURNALISM + SPEAKING split */}
      <section className="bg-cream py-24 md:py-32" data-testid="practice-split-section">
        <div className="container-vk grid lg:grid-cols-2 gap-10">
          <Link
            to="/journalism"
            data-testid="practice-card-journalism"
            className="group relative block overflow-hidden border border-paperline hover:border-burgundy transition-colors"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.journalismScene}
                alt=""
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="p-8 md:p-10 bg-cream">
              <p className="overline mb-3">Journalism</p>
              <h3 className="heading-display text-3xl md:text-4xl font-semibold leading-tight">
                A decade in the press corps &mdash; and the archives to prove it.
              </h3>
              <p className="mt-4 text-ink/70">
                Presidential coverage, governance investigations, climate reporting
                and award-winning feature work.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-burgundy text-sm uppercase tracking-[0.2em] font-semibold">
                Open the archive <ArrowRight size={16} />
              </span>
            </div>
          </Link>

          <Link
            to="/speaking"
            data-testid="practice-card-speaking"
            className="group relative block overflow-hidden border border-paperline hover:border-burgundy transition-colors"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.speakingPodium}
                alt=""
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="p-8 md:p-10 bg-cream">
              <p className="overline mb-3">Speaking</p>
              <h3 className="heading-display text-3xl md:text-4xl font-semibold leading-tight">
                On journalism, migration, leadership and the climate of our lives.
              </h3>
              <p className="mt-4 text-ink/70">
                Keynotes and conversations for newsrooms, hospitals, foundations
                and civic gatherings worldwide.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-burgundy text-sm uppercase tracking-[0.2em] font-semibold">
                Invite Victor <ArrowRight size={16} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* FOUNDATION strip */}
      <section
        data-testid="foundation-strip-section"
        className="relative bg-[#0F172A] text-cream py-24 md:py-32 overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 mix-blend-overlay"
          style={{
            backgroundImage: `url(${IMAGES.foundation})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="container-vk relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="overline text-gold mb-5">Delinam Foundation</p>
            <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-balance">
              Climate change is the next great public-health crisis. The
              Foundation is building the response &mdash; one community at a time.
            </h2>
            <p className="mt-6 text-cream/75 max-w-2xl leading-relaxed">
              Field clinics, scholarships for frontline workers, and reporting
              fellowships for African journalists covering the climate-health
              frontier.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/foundation"
              data-testid="foundation-strip-cta"
              className="inline-flex items-center gap-2 bg-gold text-ink px-7 py-4 font-medium hover:bg-cream transition-colors"
            >
              Learn About the Initiative <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* STARTING AGAIN — Canada transition bridge */}
      <section
        data-testid="starting-again-section"
        className="bg-cream py-24 md:py-36 border-t border-paperline"
      >
        <div className="container-vk">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Editorial column */}
            <div className="lg:col-span-7 order-2 lg:order-1">
              <p className="overline mb-6">Starting Again</p>
              <h2 className="heading-display text-4xl sm:text-5xl lg:text-[3.75rem] font-semibold leading-[1.02] tracking-tight text-balance">
                The second chapter began on a{" "}
                <span className="italic text-burgundy">factory floor</span>.
              </h2>
              <div className="mt-10 space-y-6 text-lg leading-relaxed text-ink/80 max-w-2xl">
                <p>
                  After covering presidents, parliament, courts, and national
                  affairs in Ghana, Victor arrived in Canada and joined
                  thousands of skilled newcomers rebuilding their lives from
                  the ground up.
                </p>
                <p>
                  Manufacturing floors, warehouses, forklifts, and reach trucks
                  became part of a new education in resilience, adaptation, and
                  determination. These experiences would later shape his work
                  in healthcare, advocacy, and public service.
                </p>
              </div>

              <div className="ornament-rule mt-12 max-w-md">
                <span className="text-xs uppercase tracking-[0.22em]">
                  Bridge Chapter
                </span>
              </div>

              {/* Watch the Journey gallery */}
              <div className="mt-12" data-testid="watch-the-journey-gallery">
                <p className="overline mb-5">Watch the Journey</p>
                <div className="grid sm:grid-cols-2 gap-6">
                  {JOURNEY_VIDEOS.map((v) => (
                    <VideoLightbox
                      key={v.id}
                      src={v.src}
                      poster={v.poster}
                      title={v.title}
                      description={v.description}
                      aspect="aspect-video"
                      testId={`journey-video-${v.id}`}
                    />
                  ))}
                </div>
                <p
                  className="mt-6 italic font-serif text-ink/65 leading-relaxed max-w-xl"
                  data-testid="watch-the-journey-caption"
                >
                  &ldquo;Learning new systems. Building new skills. Starting
                  again without starting over.&rdquo;
                </p>
              </div>
            </div>

            {/* Featured image */}
            <div className="lg:col-span-5 lg:sticky lg:top-28 order-1 lg:order-2">
              <ZoomableImage
                src={IMAGES.factoryFloor}
                alt="Victor Kwawukume working on a manufacturing line in Canada"
                captionOverline="Manufacturing floor · Canada"
                caption="Among the thousands of skilled newcomers rebuilding from the ground up."
                aspect="aspect-[4/5]"
                testId="starting-again-primary-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* AWARDS / TRUST STRIP */}
      <section className="bg-cream py-20 md:py-24" data-testid="awards-strip">
        <div className="container-vk">
          <p className="overline mb-8 text-center">Recognition</p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {AWARDS.map((a) => (
              <li
                key={a}
                className="border border-paperline p-6 text-sm uppercase tracking-[0.14em] text-ink/75 leading-relaxed"
              >
                {a}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-cream-deep py-24 md:py-32" data-testid="contact-cta-section">
        <div className="container-vk text-center max-w-3xl mx-auto">
          <p className="overline mb-5">Get in touch</p>
          <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-balance">
            For speaking, press, partnerships or simply a good question &mdash; the door is open.
          </h2>
          <Link
            to="/contact"
            data-testid="home-contact-cta"
            className="btn-primary mt-10"
          >
            Write to Victor <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function BookTile({ book, className = "", featured = false }) {
  return (
    <Link
      to={`/books#${book.slug}`}
      data-testid={`book-tile-${book.slug}`}
      className={`group animate-fade-up relative overflow-hidden border border-paperline hover:border-burgundy transition-colors bg-cream ${className}`}
    >
      <div className="grid grid-cols-1 h-full">
        <div className="relative overflow-hidden bg-cream-deep aspect-square">
          <img
            src={book.cover}
            alt={`${book.title} — book cover`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
          />
        </div>
        <div className="p-7 md:p-9 flex flex-col justify-between gap-6">
          <div>
            <p className="overline mb-3">Forthcoming</p>
            <h3 className={`heading-display ${featured ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"} font-semibold leading-[1.05]`}>
              {book.title}
            </h3>
            <p className="mt-3 text-ink/70 text-sm md:text-base italic">
              {book.tagline}
            </p>
          </div>
          <span className="inline-flex items-center gap-2 text-burgundy text-xs uppercase tracking-[0.22em] font-semibold">
            Read more →
          </span>
        </div>
      </div>
    </Link>
  );
}
